import { createRouter, createWebHistory } from "vue-router"
import { useCabinetStore } from "@/stores/cabinetStore.js"
import { useBasketStore } from "@/stores/basketStore.js"
import { refreshCsrfToken } from "@/js-modules/csrfToken.js"
import { prefersV3, redirectToV3 } from "@/js-modules/preferV3.js"

// Eager Loaded Routes
import CabinetsPage from "@/components/cabinets-page/CabinetsPage.vue"
import SearchPage from "@/components/search-page/SearchPage.vue"
import AdminPage from "@/components/admin-page/AdminPage.vue";

// Lazy Loaded Routes
const DocViewerPage = () => import("@/components/doc-viewer-page/DocViewerPage.vue")
const CabinetSettingsPage = () => import('@/components/cabinet-settings-page/CabinetSettingsPage.vue')
const CabinetGeneralSettings = () => import('@/components/cabinet-settings-page/CabinetGeneralSettings.vue')
const CabinetManageMembers = () => import("@/components/cabinet-settings-page/members/CabinetManageMembers.vue")
const CabinetManageMetadata = () => import("@/components/cabinet-settings-page/metadata/CabinetManageMetadata.vue")
const CabinetManageLookups = () => import("@/components/cabinet-settings-page/lookups/CabinetManageLookups.vue")
const CabinetUploadsEditor = () => import("@/components/cabinet-settings-page/CabinetUploadsEditor.vue")
const CabinetDocUpload = () => import("@/components/cabinet-settings-page/CabinetDocUpload.vue")
const CabinetDocDownload = () => import("@/components/cabinet-settings-page/CabinetDocDownload.vue")
const CabinetRetention = () => import("@/components/cabinet-settings-page/CabinetRetention.vue")
const CabinetRedaction = () => import("@/components/cabinet-settings-page/CabinetRedaction.vue")
const DocSplitterPage = () => import("@/components/doc-splitter-page/DocSplitterPage.vue")
const SiteConfiguration = () => import("@/components/admin-page/SiteConfiguration.vue")
const UserManagement = () => import("@/components/admin-page/users/UserManagement.vue")
const CabinetRoleManagement = () => import("@/components/admin-page/roles/CabinetRoleManagement.vue")
const RegistrationPage = () => import("@/components/base-components/auth/RegistrationPage.vue")
const LoginPage = () => import("@/components/base-components/auth/LoginPage.vue")
const ForgotPasswordPage = () => import("@/components/base-components/auth/ForgotPasswordPage.vue")
const ResetPasswordPage = () => import("@/components/base-components/auth/ResetPasswordPage.vue")
const UserDownloadPage = () => import("@/components/user-downloads-page/UserDownloadPage.vue")

const routes = [
  {
    path: "/auth",
    meta: { publicPage: true }, // auth pages don't require a user to be signed in
    children: [
      { name: "login", path: "login", component: LoginPage },
      { name: "register", path: "register", component: RegistrationPage },
      { name: "forgotPassword", path: "forgotPassword", component: ForgotPasswordPage },
      { name: "resetPassword", path: "resetPassword", component: ResetPasswordPage }
    ]
  },
  { name: "cabinets", path: "/", component: CabinetsPage },
  { name: "search", path: "/cabinets/:cabinetId", component: SearchPage },
  {
    name: "basketViewer",
    path: "/cabinets/:cabinetId/docViewer/:basketId/:basketIndex",
    component: DocViewerPage,
  },
  { name: 'docSplitter', path: '/cabinets/:cabinetId/docSplitter/:basketId/:basketIndex', component: DocSplitterPage },
  {
    name: "admin",
    path: "/admin",
    component: AdminPage,
    redirect: { name: 'siteConfig' },
    children: [
      { name: "siteConfig", path: "siteConfig", component: SiteConfiguration },
      { name: "userControl", path: "userControl", component: UserManagement },
      { name: "accessControl", path: "accessControl", component: CabinetRoleManagement }
    ]
  },
  { name: "userDownloads", path: "/userDownloads", component: UserDownloadPage },
  {
    name: "cabinetSettings",
    path: "/cabinets/:cabinetId/settings/",
    component: CabinetSettingsPage,
    redirect: { name: 'cabinetGeneralSettings' },
    children: [
      { name: "cabinetGeneralSettings", path: "generalSettings", component: CabinetGeneralSettings },
      { name: "cabinetManageMembers", path: "members", component: CabinetManageMembers },
      { name: "cabinetManageMetadata", path: "metadata", component: CabinetManageMetadata },
      { name: "cabinetManageLookups", path: "lookups", component: CabinetManageLookups },
      {
        name: "uploadsMetadata",
        path: "uploadsMetadata",
        component: CabinetUploadsEditor,
        children: [
          {
            name: "metadataChooseUpload",
            path: "pastUploads",
            component: null,
          },
          {
            name: "metadataUploadApply",
            path: "applyMetadata",
            component: null,
          },
        ],
      },
      { name: "cabinetUpload", path: "upload", component: CabinetDocUpload },
      { name: "cabinetDownload", path: "download", component: CabinetDocDownload },
      { name: "cabinetRetention", path: "retention", component: CabinetRetention },
      { name: "cabinetRedaction", path: "redaction", component: CabinetRedaction },
    ],
  }
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to, from) => {
  // Opted into React: classic home always goes to /v3.
  if (prefersV3() && to.path === "/") {
    redirectToV3()
    return false
  }

  // Refresh CSRF token when navigating to auth pages (public pages)
  // This ensures we have a fresh token after session expiration
  if (to.meta.publicPage) {
    await refreshCsrfToken()
  }

  // Set cabinet and basket state for routes that need them
  const cabinetStore = useCabinetStore()
  cabinetStore.setCabinet(Number(to.params.cabinetId))

  const basketStore = useBasketStore()
  basketStore.setBasket(Number(to.params.basketId))
  basketStore.setBasketIndex(Number(to.params.basketIndex))
})

export default router
